import type { UserManager } from 'oidc-client-ts';

import { config } from '../../config';
import { trace } from './trace';
import * as storage from '../login/storage';
import { reportErrorToSentry } from './sentry';

const saveCurrentRoute = () => {
    const initialRoute = [window.location.hash, window.location.search].join('').replace(/^#\/?/u, '');

    if (initialRoute) {
        storage.routeStorage.saveRoute(initialRoute);
        trace('initialRoute: saving initial route', initialRoute);
    }
};

const doSignin = (userManager: UserManager, isAuthCodeRedirect: boolean) => {
    if (isAuthCodeRedirect) {
        return userManager.signinCallback().catch((e: unknown) => {
            if (e instanceof Error && e.message.includes('No matching state found in storage')) {
                return userManager.signinSilent();
            }
            throw e;
        });
    }
    return userManager.signinSilent();
};

export const attemptInitialSignIn = async (userManager: UserManager) => {
    const isAuthCodeRedirect =
        window.location.href.startsWith(config.login.redirectUri as string) &&
        /[?&]code=[^&]*/.test(window.location.search);

    try {
        const user = await doSignin(userManager, isAuthCodeRedirect);
        const initialRoute = storage.routeStorage.getRoute();

        trace('initialRoute lookup', initialRoute);

        if (isAuthCodeRedirect && initialRoute !== null) {
            trace(`Go to location "/${initialRoute}"`);
            window.history.replaceState({}, '', `${window.location.origin}/#/${initialRoute}`);
        } else if (isAuthCodeRedirect) {
            window.history.replaceState({}, '', window.location.origin);
        }
        storage.routeStorage.discardRoute();
        return user;
    } catch (error) {
        trace('oidc.signinSilent failed, trying page redirect...', error);

        if (config.login.preventRedirect) {
            console.warn('[feature/login] redirect prevented due to config. Error was', error);
        } else if (isAuthCodeRedirect) {
            trace('oidc.signinSilent.error', 'redirect prevented due to suspicious signin error', error);
            storage.routeStorage.discardRoute();
            reportErrorToSentry(error);
        } else {
            saveCurrentRoute();
            await userManager.signinRedirect();
            return undefined;
        }

        trace('auth problem?', error);
        throw new Error('Need to sign in');
    }
};
